import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryUploader from "@/components/admin/GalleryUploader";
import VideoUrlList from "@/components/admin/VideoUrlList";
import { toast } from "sonner";

type Post = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  cover_image_path: string | null;
  image_paths: string[];
  video_urls: string[];
  published: boolean;
  published_at: string | null;
  sort_order: number;
};

type Editing = Partial<Post> & { id?: string };

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);

const empty = (): Editing => ({
  slug: "",
  title: "",
  subtitle: "",
  body: "",
  cover_image_path: null,
  image_paths: [],
  video_urls: [],
  published: false,
  published_at: null,
  sort_order: 0,
});

const AdminBlog = () => {
  const [list, setList] = useState<Post[]>([]);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("sort_order")
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setList((data as any) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.title) return toast.error("Title required");
    const slug = (editing.slug || slugify(editing.title || "")).trim();
    if (!slug) return toast.error("Slug required");
    const payload: any = {
      slug,
      title: editing.title,
      subtitle: editing.subtitle || null,
      body: editing.body || null,
      cover_image_path: editing.cover_image_path ?? null,
      image_paths: editing.image_paths ?? [],
      video_urls: (editing.video_urls ?? []).filter((v) => v.trim().length > 0),
      published: !!editing.published,
      published_at:
        editing.published && !editing.published_at
          ? new Date().toISOString()
          : editing.published_at ?? null,
      sort_order: editing.sort_order ?? 0,
    };
    if (editing.id) {
      const { error } = await supabase.from("blog_posts").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("blog_posts").insert(payload);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved");
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl">Athlete Spotlight</h1>
          <p className="text-sm text-muted-foreground mt-1">{list.length} posts</p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
        >
          + New post
        </button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {list.map((p) => (
            <div key={p.id} className="bg-card rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold">{p.title}</p>
                  {p.subtitle && <p className="text-xs text-muted-foreground mt-0.5">{p.subtitle}</p>}
                </div>
                <span
                  className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${
                    p.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {p.published ? "Live" : "Draft"}
                </span>
                <button onClick={() => setEditing(p)} className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold">
                  Edit
                </button>
                <button onClick={() => remove(p.id)} className="text-sm font-semibold text-destructive">
                  Delete
                </button>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">No posts yet.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-[1.5rem] shadow-bold max-w-2xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "New"} post</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground">
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <Field label="Title *">
                <input
                  value={editing.title ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      title: e.target.value,
                      slug: editing.id || editing.slug ? editing.slug : slugify(e.target.value),
                    })
                  }
                  className={inp}
                  maxLength={200}
                />
              </Field>
              <Field label="Subtitle">
                <input
                  value={editing.subtitle ?? ""}
                  onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                  className={inp}
                  maxLength={300}
                />
              </Field>
              <Field label="Slug *">
                <input
                  value={editing.slug ?? ""}
                  onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                  className={inp}
                />
              </Field>
              <Field label="Body">
                <RichTextEditor value={editing.body ?? ""} onChange={(v) => setEditing({ ...editing, body: v })} />
              </Field>
              <Field label="Cover image">
                <ImageUploader
                  value={editing.cover_image_path ?? null}
                  onChange={(p) => setEditing({ ...editing, cover_image_path: p })}
                  prefix="blog-covers"
                />
              </Field>
              <Field label="Additional images (up to 10)">
                <GalleryUploader
                  value={(editing.image_paths ?? []).slice(0, 10)}
                  onChange={(paths) => setEditing({ ...editing, image_paths: paths.slice(0, 10) })}
                  prefix="blog-gallery"
                />
              </Field>
              <Field label="Videos (up to 5)">
                <VideoUrlList
                  value={editing.video_urls ?? []}
                  onChange={(v) => setEditing({ ...editing, video_urls: v })}
                  max={5}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order">
                  <input
                    type="number"
                    value={editing.sort_order ?? 0}
                    onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })}
                    className={inp}
                  />
                </Field>
                <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.published}
                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                  <span className="font-semibold text-sm">Published</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">
                Cancel
              </button>
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminBlog;
